import { useCallback, useEffect, useRef, useState } from "react";

/** Ekranların ortak otomatik yenileme aralığı (ms). */
export const AUTO_REFRESH_MS = 45000;

/**
 * Belirli aralıklarla sessiz yenileme çalıştırır.
 *
 * Yenileme **filtreyi, sayfayı ve seçimleri bozmaz**: bu hook yalnızca zamanlayıcıyı
 * yönetir, ne çekileceğine çağıran karar verir. Çağıran, o anki filtre/sayfa
 * durumunu kullanan bir fonksiyon geçmelidir (tam sayfa spinner'ı göstermeyen
 * "sessiz" varyant).
 *
 * Şunlarda tetiklenmez:
 *  - Sekme arka plandayken (`document.hidden`) — kimsenin bakmadığı ekran için
 *    istek atmanın anlamı yok; sekmeye dönülünce bir sonraki turda yenilenir.
 *  - `enabled` false iken — modal açıkken ya da oturum uyarısı görünürken
 *    listenin altından veri değişmesin diye.
 *  - Önceki yenileme hâlâ sürüyorken — yavaş bağlantıda istekler üst üste binmez.
 *
 * @param {Function} onRefresh Sessiz yenileme fonksiyonu (Promise dönebilir).
 * @param {{ intervalMs?: number, enabled?: boolean }} [options]
 * @returns {{ refreshing: boolean, lastRefreshedAt: Date|null, refreshNow: Function }}
 *   Ekranın küçük bir "son güncelleme" göstergesi çizebilmesi için durum döner;
 *   refreshNow kullanıcının elle tetiklemesi içindir.
 */
export const useAutoRefresh = (onRefresh, { intervalMs = AUTO_REFRESH_MS, enabled = true } = {}) => {
    // Callback ref'te tutulur: her render'da yeni fonksiyon gelse bile zamanlayıcı
    // sıfırlanmaz, aksi hâlde 45 sn hiç dolmayabilirdi.
    const savedRef = useRef(onRefresh);
    const runningRef = useRef(false);
    const [refreshing, setRefreshing] = useState(false);
    const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

    useEffect(() => { savedRef.current = onRefresh; }, [onRefresh]);

    const run = useCallback(async () => {
        if (runningRef.current) return;
        runningRef.current = true;
        setRefreshing(true);
        try {
            await savedRef.current?.();
            setLastRefreshedAt(new Date());
        } catch {
            // Otomatik yenileme sessizdir: hata kullanıcıya gösterilmez,
            // ekrandaki veri korunur ve bir sonraki turda tekrar denenir.
            // Zaman damgası da güncellenmez; gösterge eski değeri korur.
        } finally {
            runningRef.current = false;
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        if (!enabled) return undefined;
        const tick = () => { if (!document.hidden) run(); };
        const id = setInterval(tick, intervalMs);
        return () => clearInterval(id);
    }, [enabled, intervalMs, run]);

    return { refreshing, lastRefreshedAt, refreshNow: run };
};

export default useAutoRefresh;
